/**
 * BUYKEYS.TSX - Compact Key Purchase Component
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { getGameContract, getFrameContract } from '../utils/contract';
import { ethers } from 'ethers';
import { fetchFramePrice, formatFrameToUSD } from '../utils/price';
import { FACTIONS, getFactionByContractId } from '../config/factions';
import { useDemoMode } from './DemoMode';
import { THALIAInline } from './THALIAIcon';
import { SocialSharing } from './SocialSharing';
import { BuyKeysWarnings } from './BuyKeysWarnings';

// Fire-and-forget XP event to game-engine worker
function postGameEvent(address: string, type: string, data: Record<string, unknown>) {
  fetch('https://nt3d-game-engine.wurkit.workers.dev/player/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address, type, ...data }),
  }).catch(() => {}); // silently ignore — non-critical
}

interface BuyKeysProps {
  account: string | null;
  currentPrice: bigint;
  onPurchase?: () => void;
  referrerFromUrl?: string | null;
  currentRoundId?: number;
  // Game state flags for contextual warnings
  fomoSurgeActive?: boolean;
  flashCrashActive?: boolean;
  isAssassinMode?: boolean;
  timeRemaining?: number;
}

const SMALL_KEY_AMOUNTS = [1, 2, 3, 4, 5];
const QUICK_KEY_AMOUNTS = [10, 50, 100, 250];
// Contract enforces MAX_KEYS_PER_ADDRESS_PER_BLOCK = 500 in NT3DAntiManipulation.sol
const MAX_KEYS_PER_BLOCK = 500n;

export default function BuyKeys({ 
  account, 
  currentPrice, 
  onPurchase, 
  referrerFromUrl,
  currentRoundId,
  fomoSurgeActive = false,
  flashCrashActive = false,
  isAssassinMode = false,
  timeRemaining = -1,
}: BuyKeysProps) {
  // Demo mode
  const demoMode = useDemoMode();
  
  // State
  const [pawAmount, setPawAmount] = useState<string>('');
  const [selectedFaction, setSelectedFaction] = useState<number>(1); // Contract ID 1 = Raven
  const [referrer, setReferrer] = useState<string>('');
  const [purchaseMessage, setPurchaseMessage] = useState<string>('');
  const [showMessageInput, setShowMessageInput] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [allowance, setAllowance] = useState<bigint>(0n);
  const [lockedFaction, setLockedFactionState] = useState<number | null>(() => {
    // Restore from sessionStorage to survive component remounts (tab switches)
    const stored = sessionStorage.getItem('nt3d_locked_faction');
    return stored ? Number(stored) : null;
  });
  const [cascadeLevel, setCascadeLevel] = useState<number>(0); // Was comboCount
  const [framePrice, setFramePrice] = useState<number>(0);
  const [ethBalance, setEthBalance] = useState<bigint | null>(null);

  // Wrapper that also persists to sessionStorage (stores round ID to detect new-round resets)
  const setLockedFaction = useCallback((factionId: number | null, roundId?: number) => {
    setLockedFactionState(factionId);
    if (factionId !== null) {
      sessionStorage.setItem('nt3d_locked_faction', String(factionId));
      sessionStorage.setItem('nt3d_locked_faction_round', String(roundId ?? currentRoundId ?? 0));
    } else {
      sessionStorage.removeItem('nt3d_locked_faction');
      sessionStorage.removeItem('nt3d_locked_faction_round');
    }
  }, [currentRoundId]);
  const [lastPurchaseData, setLastPurchaseData] = useState<{ keys: string; amount: string } | null>(null);
  // Tracks the intended key count when a preset button is clicked.
  // Cleared when user manually edits Frame input. Prevents the
  // (pawAmount / currentPrice) estimate from inflating the display.
  const [targetKeyCount, setTargetKeyCount] = useState<bigint | null>(null);

  // Fetch Frame price
  useEffect(() => {
    const updatePrice = async () => {
      const price = await fetchFramePrice();
      setFramePrice(price);
    };
    updatePrice();
    const interval = setInterval(updatePrice, 60000);
    return () => clearInterval(interval);
  }, []);

  // Use referrer from URL if provided
  useEffect(() => {
    if (referrerFromUrl && !referrer) {
      setReferrer(referrerFromUrl);
    }
  }, [referrerFromUrl, referrer]);

  // Fetch allowance and player data
  const fetchData = useCallback(async () => {
    if (!account) return;
    try {
      const [gameContract, frameContract] = await Promise.all([getGameContract(), getFrameContract()]);
      if (!frameContract || !gameContract) return;
      
      const gameAddress = await gameContract.getAddress();
      const currentAllowance = await frameContract.allowance(account, gameAddress);
      
      // Only update allowance if it increased, or if current state is 0
      // This prevents RPC lag from overwriting a known MaxUint256 approval
      setAllowance(prev => {
        if (prev === 0n || currentAllowance >= prev) {
          return currentAllowance;
        }
        // If RPC returns lower value but we had higher, keep higher (RPC lag)
        return prev;
      });

      // Get player's locked faction and cascade
      try {
        const playerData = await gameContract.getPlayerInfo(account);
        // getPlayerInfo returns (keysOwned, teamId, lastPurchaseTime, referralEarnings, lifetimeKeys, lifetimeSpent, roundsWon, sniperKills)
        const keysOwned = playerData.keysOwned;
        const factionId = Number(playerData.teamId);
        const cascade = 0; // cascade tracking removed from current contract
        
        // Check if round changed (stale lock)
        const persistedRound = sessionStorage.getItem('nt3d_locked_faction_round');
        const isStaleRound = currentRoundId !== undefined && persistedRound !== null &&
          Number(persistedRound) < currentRoundId;
        
        if (isStaleRound) {
          // New round started - clear lock and let user choose
          console.log(`[BuyKeys] New round detected (${persistedRound} → ${currentRoundId}), clearing faction lock`);
          setLockedFaction(null);
        } else if (keysOwned > 0n && factionId > 0) {
          // Player has keys in current round - lock to their faction
          setLockedFaction(factionId, currentRoundId);
          setSelectedFaction(factionId);
        } else if (keysOwned === 0n) {
          // No keys in this round - clear any stale lock
          setLockedFaction(null);
        }
        setCascadeLevel(cascade);
      } catch (e) {
        console.warn('[BuyKeys] Could not fetch player data:', e);
        // Don't clear lockedFaction on error — keep any persisted value
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [account, currentRoundId]);

  // Eagerly clear a stale lock when the round advances (before fetchData round-trips)
  useEffect(() => {
    if (currentRoundId === undefined) return;
    const persistedRound = sessionStorage.getItem('nt3d_locked_faction_round');
    if (persistedRound !== null && Number(persistedRound) < currentRoundId) {
      setLockedFaction(null);
    }
  }, [currentRoundId, setLockedFaction]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch ETH balance for gas warnings
  useEffect(() => {
    const fetchEthBalance = async () => {
      if (!account || !window.ethereum) { // window.ethereum check kept for brevity — BrowserProvider will use getPreferredProvider via contract utils
        setEthBalance(null);
        return;
      }
      try {
        const { getPreferredProvider: getProv } = await import('../utils/provider');
        const eth = getProv();
        if (!eth) throw new Error('No wallet provider');
        const provider = new ethers.BrowserProvider(eth as any);
        const balance = await provider.getBalance(account);
        setEthBalance(balance);
      } catch (e) {
        console.warn('[BuyKeys] Could not fetch ETH balance:', e);
      }
    };
    fetchEthBalance();
    const interval = setInterval(fetchEthBalance, 30000); // Update every 30s
    return () => clearInterval(interval);
  }, [account]);

  // Calculations
  const pawAmountBigInt = useMemo(() => {
    try {
      const parsed = parseFloat(pawAmount);
      if (isNaN(parsed) || parsed <= 0) return 0n;
      return ethers.parseEther(pawAmount);
    } catch { return 0n; }
  }, [pawAmount]);

  const estimatedKeys = useMemo(() => {
    if (currentPrice <= 0n || pawAmountBigInt <= 0n) return { post: 0n, bonus: false };
    const keys = pawAmountBigInt / currentPrice;
    const bonus = keys >= 100n;
    return { post: bonus ? keys + (keys / 20n) : keys, bonus };
  }, [pawAmountBigInt, currentPrice]);

  // Use targetKeyCount (with bonus) when set by a preset button;
  // fall back to the estimated value when user typed a raw PAW amount.
  const displayKeyCount = useMemo(() => {
    if (targetKeyCount !== null) {
      const bonus = targetKeyCount >= 100n;
      return bonus ? targetKeyCount + (targetKeyCount / 20n) : targetKeyCount;
    }
    return estimatedKeys.post;
  }, [targetKeyCount, estimatedKeys.post]);
  const displayBonus = targetKeyCount !== null ? targetKeyCount >= 100n : estimatedKeys.bonus;

  const needsApproval = allowance < pawAmountBigInt;

  // Parse transaction errors into user-friendly messages
  const parseTransactionError = (error: any): string => {
    const errorString = error?.reason || error?.message || String(error);
    console.error('[parseTransactionError] raw error:', error);
    console.error('[parseTransactionError] errorString:', errorString);
    console.error('[parseTransactionError] error.code:', error?.code);
    console.error('[parseTransactionError] error.data:', error?.data);

    // Direct selector lookup — catches custom errors even when ethers can't decode them
    // (e.g. CALL_EXCEPTION from estimateGas through BrowserProvider)
    const rawData: string = typeof error?.data === 'string'
      ? error.data
      : typeof error?.data?.data === 'string'
        ? error.data.data
        : '';
    const selector = rawData.slice(0, 10).toLowerCase();
    const SELECTOR_MESSAGES: Record<string, string> = {
      '0x27588df3': 'Purchase limit reached. You\'ve hit the max keys per block. Try a smaller amount or wait for the next block.',
      '0xc864f41f': 'Too many timer extensions in this block. Wait for the next block.',
      '0x4c39f73a': 'Purchase too fast. Wait a moment before buying again.',
      '0xd1544b7c': 'Commit not found. Please refresh and try again.',
      '0x1a74087f': 'Commit not ready. Wait a moment before revealing.',
      '0xa13644b8': 'Commit expired. Please start the purchase process again.',
      '0xd331d27e': 'Commit already revealed.',
      '0x45f8726a': 'Same block reveal not allowed. Wait for the next block.',
      '0x1f119602': 'Round is ending. Wait for the new round to start.',
      '0x3f126bad': 'Insufficient PAW. You need more PAW tokens to complete this purchase.',
      '0x0a0b0d79': 'Transaction validation failed. Check your inputs and try again.',
      '0xdedf8ebe': 'Invalid faction selected.',
    };
    if (selector && SELECTOR_MESSAGES[selector]) {
      return SELECTOR_MESSAGES[selector];
    }

    // Build a wide search string from all known ethers v6 error fields so
    // we can match against the deepest available message.
    const deepString = [
      errorString,
      error?.data?.message,
      error?.info?.error?.message,
      error?.info?.error?.data,
      error?.shortMessage,
      JSON.stringify(error?.data ?? ''),
    ].filter(Boolean).join(' ').toLowerCase();
    
    // Common revert reasons — use deepString so nested errors (error.info.error.message etc.) are matched
    if (deepString.includes('insufficient') && deepString.includes('balance')) {
      return 'Insufficient PAW balance. Check your wallet balance.';
    }
    if (deepString.includes('exceeds allowance') || deepString.includes('not approved')) {
      return 'PAW not approved. Please approve PAW spending first.';
    }
    if (deepString.includes('round ended') || deepString.includes('round not active')) {
      return 'Round has ended. Wait for a new round to start.';
    }
    if (deepString.includes('faction') && deepString.includes('locked')) {
      return 'Wrong faction selected. You must buy with your locked faction.';
    }
    if (deepString.includes('user rejected') || deepString.includes('user denied') || error?.code === 4001) {
      return 'Transaction cancelled by user.';
    }
    // "cannot estimate gas" / "execution reverted" means the tx would revert — NOT a missing ETH problem.
    // This must be checked BEFORE the broader gas check below.
    if (deepString.includes('cannot estimate gas') || deepString.includes('execution reverted')) {
      return 'Transaction would fail. Check your PAW approval and game state.';
    }
    // Only show the ETH warning when the node explicitly says the sender has insufficient funds.
    if ((deepString.includes('insufficient funds') && deepString.includes('gas')) || deepString.includes('intrinsic gas too low')) {
      return 'Insufficient ETH for gas fees. Add more ETH to your wallet.';
    }
    if (deepString.includes('nonce')) {
      return 'Transaction nonce error. Try resetting your wallet or wait a moment.';
    }
    if (deepString.includes('timeout') || deepString.includes('network')) {
      return 'Network error. Check your connection and try again.';
    }
    if (deepString.includes('underpriced')) {
      return 'Gas price too low. Try increasing gas in your wallet.';
    }
    
    // Return original if no match
    return error?.reason || 'Transaction failed. Please try again.';
  };

  // Handlers
  const handleApprove = async () => {
    if (!account) return;
    setIsApproving(true);
    try {
      const frameContract = await getFrameContract(true);
      if (!frameContract) throw new Error('Frame contract not available');
      const gameContract = await getGameContract();
      if (!gameContract) throw new Error('Game contract not available');
      const gameAddress = await gameContract.getAddress();
      
      const tx = await frameContract.approve(gameAddress, ethers.MaxUint256);
      toast.loading('Approving FRAME...', { id: 'approve' });
      await tx.wait();
      toast.success('PAW approved!', { id: 'approve' });
      
      // Immediately set allowance to max after successful approval
      // This avoids RPC sync issues where read provider hasn't caught up
      setAllowance(ethers.MaxUint256);
      
      // Also trigger background refresh in case we need fresh player data
      setTimeout(() => fetchData(), 2000);
    } catch (error: any) {
      console.error('Approve failed:', error);
      toast.error(parseTransactionError(error), { id: 'approve' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleBuy = async () => {
    if (pawAmountBigInt <= 0n) return;
    
    // Demo mode: use simulated purchase
    if (demoMode.isDemo) {
      setIsLoading(true);
      try {
        const result = demoMode.buyKeys(pawAmountBigInt, selectedFaction);
        if (result.success) {
          toast.success(`🎮 Demo: Acquired ~${Number(result.keys)} keys!`, { id: 'buy' });
          setPawAmount('');
          setPurchaseMessage('');
          onPurchase?.();
        } else {
          toast.error(`🎮 Demo: ${result.message}`, { id: 'buy' });
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Real mode: require account
    if (!account) return;
    setIsLoading(true);
    try {
      const gameContract = await getGameContract(true);
      if (!gameContract) throw new Error('Game contract not available');
      
      const refAddress = referrer && ethers.isAddress(referrer) ? referrer : ethers.ZeroAddress;
      
      toast.loading('Acquiring keys...', { id: 'buy' });
      const tx = await gameContract.buyKeys(pawAmountBigInt, selectedFaction, refAddress, purchaseMessage);
      await tx.wait();
      
      // Immediately lock faction after purchase (record round so new-round detection works)
      setLockedFaction(selectedFaction, currentRoundId);
      
      const keysAcquired = Number(displayKeyCount).toLocaleString();
      toast.success(`Acquired ~${keysAcquired} keys!`, { id: 'buy' });
      setLastPurchaseData({ keys: keysAcquired, amount: ethers.formatEther(pawAmountBigInt) });

      // Track XP in game-engine worker (fire-and-forget)
      postGameEvent(account, 'key_purchase', {
        keysAmount: Number(displayKeyCount),
        pawSpent: ethers.formatEther(pawAmountBigInt),
        faction: selectedFaction,
        isBloodMoon: false, // not available as prop; worker will cross-reference round state
        isAssassinMode,
        isFlashCrash: flashCrashActive,
        isFomoSurge: fomoSurgeActive,
      });

      setPawAmount('');
      setPurchaseMessage('');
      onPurchase?.();
      // Delay fetchData to let blockchain state settle — immediate fetch can race
      // and return stale data (keysOwned=0) which would clear the faction lock
      setTimeout(() => fetchData(), 3000);
    } catch (error: any) {
      console.error('Buy failed:', error);
      toast.error(parseTransactionError(error), { id: 'buy' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      className="relative glass-panel-3d"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4 md:p-5 relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neon-magenta/20 to-neon-purple/20 flex items-center justify-center border border-neon-magenta/30">
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-neon-magenta">
              <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 12l8 8M16 20l2-2M18 18l2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="8" cy="8" r="2" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h2 className="font-orbitron text-lg text-neon-magenta">ACQUIRE KEYS</h2>
            <p className="text-xs text-gray-500 font-rajdhani">Delay the Singularity • Fight THALIA</p>
          </div>
        </div>
        
        {/* Input + Preview Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* PAW Input */}
          <div>
            <div className="relative">
              <input
                type="text"
                value={pawAmount}
                onChange={(e) => {
                  // Allow only numbers and dots (no commas in input - display only)
                  const raw = e.target.value.replace(/[^0-9.]/g, '');
                  setPawAmount(raw);
                  setTargetKeyCount(null); // user typed manually — clear preset target
                }}
                className="w-full px-4 py-3 rounded-xl bg-void-black/50 border border-neon-magenta/30 text-white text-xl font-orbitron text-center focus:outline-none focus:border-neon-magenta focus:shadow-[0_0_20px_rgba(255,0,128,0.3)] transition-all"
                placeholder="PAW Amount"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neon-magenta/70 font-mono font-bold">PAW</div>
            </div>
            {/* Formatted display */}
            {pawAmountBigInt > 0n && (
              <div className="text-center text-xs text-neon-magenta/70 mt-1 font-mono">
                {Number(ethers.formatEther(pawAmountBigInt)).toLocaleString(undefined, { maximumFractionDigits: 2 })} PAW
              </div>
            )}
            {/* USD Value Display */}
            {pawAmountBigInt > 0n && framePrice > 0 && (
              <div className="text-center text-sm text-gray-400 mt-1 font-mono">
                ≈ ${formatFrameToUSD(pawAmountBigInt, framePrice)} USD
              </div>
            )}
            {/* Quick key amounts - small */}
            <div className="flex gap-1.5 mt-2">
              {SMALL_KEY_AMOUNTS.map((keyCount) => {
                return (
                  <motion.button
                    key={keyCount}
                    onClick={async () => {
                      // Calculate accurate PAW cost for desired key count using bonding curve
                      try {
                        const gameContract = await getGameContract();
                        if (!gameContract) return;
                        
                        // Get current round data for totalKeysSold
                        const roundData = await gameContract.currentRound();
                        const totalKeysSold = roundData.totalKeysSold || 0n;
                        
                        // Get base price and increment from contract
                        const basePrice = await gameContract.baseKeyPrice();
                        const increment = await gameContract.keyPriceIncrement();
                        
                        // Calculate total cost using bonding curve formula
                        const n = BigInt(keyCount);
                        const totalCost = n * basePrice + increment * (n * totalKeysSold + (n * (n - 1n)) / 2n);
                        
                        // Add 2% buffer for price movement during tx
                        const costWithBuffer = (totalCost * 102n) / 100n;
                        
                        setPawAmount(ethers.formatEther(costWithBuffer));
                        setTargetKeyCount(BigInt(keyCount));
                      } catch (e) {
                        console.error('Failed to calculate key cost:', e);
                        // Fallback to simple calculation
                        if (currentPrice > 0n) {
                          const simpleCost = BigInt(keyCount) * currentPrice;
                          setPawAmount(ethers.formatEther(simpleCost));
                          setTargetKeyCount(BigInt(keyCount));
                        }
                      }
                    }}
                    className="flex-1 py-1.5 rounded-lg border text-xs font-mono transition-all bg-void-black/50 border-gray-700 text-gray-400 hover:border-neon-cyan/50 hover:bg-neon-cyan/10 hover:text-neon-cyan"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {keyCount} <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  </motion.button>
                );
              })}
            </div>
            {/* Quick key amounts - large */}
            <div className="flex gap-1.5 mt-1.5">
              {QUICK_KEY_AMOUNTS.map((keyCount) => {
                return (
                  <motion.button
                    key={keyCount}
                    onClick={async () => {
                      // Calculate accurate PAW cost for desired key count using bonding curve
                      try {
                        const gameContract = await getGameContract();
                        if (!gameContract) return;
                        
                        // Get current round data for totalKeysSold
                        const roundData = await gameContract.currentRound();
                        const totalKeysSold = roundData.totalKeysSold || 0n;
                        
                        // Get base price and increment from contract
                        const basePrice = await gameContract.baseKeyPrice();
                        const increment = await gameContract.keyPriceIncrement();
                        
                        // Calculate total cost using bonding curve formula
                        // Cost = sum of (basePrice + (totalKeysSold + i) * increment) for i = 0 to keyCount-1
                        // = keyCount * basePrice + increment * sum(totalKeysSold + i) for i = 0 to keyCount-1
                        // = keyCount * basePrice + increment * (keyCount * totalKeysSold + keyCount*(keyCount-1)/2)
                        const n = BigInt(keyCount);
                        const totalCost = n * basePrice + increment * (n * totalKeysSold + (n * (n - 1n)) / 2n);
                        
                        // Add 2% buffer for price movement during tx
                        const costWithBuffer = (totalCost * 102n) / 100n;
                        
                        setPawAmount(ethers.formatEther(costWithBuffer));
                        setTargetKeyCount(BigInt(keyCount));
                      } catch (e) {
                        console.error('Failed to calculate key cost:', e);
                        // Fallback to simple calculation
                        if (currentPrice > 0n) {
                          const simpleCost = BigInt(keyCount) * currentPrice;
                          setPawAmount(ethers.formatEther(simpleCost));
                          setTargetKeyCount(BigInt(keyCount));
                        }
                      }
                    }}
                    className="flex-1 py-1.5 rounded-lg border text-xs font-mono transition-all bg-void-black/50 border-gray-700 text-gray-400 hover:border-neon-cyan/50 hover:bg-neon-cyan/10 hover:text-neon-cyan"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {keyCount} <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  </motion.button>
                );
              })}
            </div>
          </div>
          
          {/* Preview + Faction */}
          <div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-void-black/50 border border-gray-700 mb-2">
              <span className="text-gray-400 text-sm font-rajdhani">You'll Get</span>
              <div className="text-right">
                <span className="font-orbitron text-lg text-neon-cyan">~{Number(displayKeyCount).toLocaleString()}</span>
                <span className="text-xs text-gray-500 ml-1">keys</span>
                {displayBonus && <span className="text-xs text-neon-cyan ml-2 animate-pulse">+5%!</span>}
              </div>
            </div>
            {/* Faction grid - 2 rows x 4 cols */}
            <div className="grid grid-cols-4 gap-2">
              {FACTIONS.map((faction) => {
                const isLocked = lockedFaction !== null && lockedFaction !== faction.contractId;
                const isSelected = selectedFaction === faction.contractId;
                return (
                  <motion.button
                    key={faction.id}
                    onClick={() => !isLocked && setSelectedFaction(faction.contractId)}
                    disabled={isLocked}
                    className={`flex flex-col items-center justify-center rounded-lg transition-all p-1.5 h-16 ${
                      isLocked ? 'opacity-30 cursor-not-allowed' : 
                      isSelected ? 'bg-neon-cyan/20 border-2 border-neon-cyan shadow-[0_0_15px_rgba(0,255,255,0.4)]' : 'bg-void-black/50 border border-transparent hover:border-gray-600'
                    }`}
                    whileHover={!isLocked ? { scale: 1.05 } : {}}
                    whileTap={!isLocked ? { scale: 0.95 } : {}}
                    title={`${faction.name} - ${faction.power}`}
                  >
                    <div className="relative">
                      <img 
                        src={faction.icon} 
                        alt={faction.name} 
                        className={`w-10 h-10 object-cover rounded-lg shadow-lg ${isLocked ? 'grayscale' : ''}`}
                        loading="lazy"
                        style={{ filter: isLocked ? 'brightness(0.4) grayscale(1)' : isSelected ? 'brightness(1.2) drop-shadow(0 0 8px rgba(0, 255, 255, 0.6))' : 'brightness(0.9)' }}
                      />
                      {isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-gray-500 text-lg">🔒</span>
                        </div>
                      )}
                      {isSelected && lockedFaction === faction.contractId && (
                        <div className="absolute -top-1 -right-1 bg-neon-cyan rounded-full w-4 h-4 flex items-center justify-center">
                          <span className="text-[8px]">✓</span>
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] mt-0.5 font-mono ${isLocked ? 'text-gray-600' : 'text-gray-400'}`}>
                      {isLocked ? '🔒' : faction.name}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            {/* Faction power info */}
            {selectedFaction > 0 && (
              <div className="mt-2 p-2 rounded-lg bg-void-black/30 border border-green-700/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neon-cyan font-mono">{getFactionByContractId(selectedFaction)?.power}</span>
                </div>
                <p className="text-[10px] text-green-400/70 mt-1 italic flex items-center gap-1">
                  <THALIAInline /> "{getFactionByContractId(selectedFaction)?.thaliaQuote}"
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Referrer input */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="Referrer address (optional)"
            value={referrer}
            onChange={(e) => setReferrer(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-void-black/50 border border-gray-700 text-white text-sm font-mono placeholder:text-gray-600 focus:outline-none focus:border-neon-purple focus:shadow-[0_0_15px_rgba(153,50,204,0.2)] transition-all"
          />
        </div>

        {/* Purchase message toggle & input */}
        <div className="mb-4">
          <button
            onClick={() => setShowMessageInput(!showMessageInput)}
            className="text-xs text-gray-500 hover:text-neon-cyan transition-colors flex items-center gap-1"
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3">
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {showMessageInput ? 'Hide message' : 'Add message to purchase'}
          </button>
          {showMessageInput && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2"
            >
              <input
                type="text"
                placeholder="Your message (shows in activity feed)"
                value={purchaseMessage}
                onChange={(e) => setPurchaseMessage(e.target.value.slice(0, 100))}
                maxLength={100}
                className="w-full px-3 py-2 rounded-lg bg-void-black/50 border border-neon-cyan/30 text-white text-sm font-rajdhani placeholder:text-gray-600 focus:outline-none focus:border-neon-cyan focus:shadow-[0_0_15px_rgba(0,255,255,0.2)] transition-all"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>💬 Message will be stored on-chain forever!</span>
                <span>{purchaseMessage.length}/100</span>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* CASCADE indicator */}
        {cascadeLevel > 0 && (
          <div className="mb-3 p-2 rounded-lg bg-neon-purple/10 border border-neon-purple/30 flex items-center justify-between">
            <span className="text-sm text-neon-purple font-rajdhani flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="currentColor"/></svg>
              CASCADE x{cascadeLevel}
            </span>
            <span className="text-xs text-gray-400">+{Math.min(cascadeLevel * 5, 15)}% variance</span>
          </div>
        )}
        
        {/* Low ETH warning - gas fees */}
        {account && ethBalance !== null && ethBalance < ethers.parseEther('0.001') && (
          <div className="mb-3 p-2 rounded-lg bg-amber-900/30 border border-amber-500/50 flex items-center gap-2">
            <span className="text-amber-400 text-base">⚠️</span>
            <div className="flex-1">
              <span className="text-xs text-amber-300 font-rajdhani">
                Low ETH balance ({parseFloat(ethers.formatEther(ethBalance)).toFixed(5)} ETH)
              </span>
              <p className="text-[10px] text-amber-400/70 mt-0.5">
                You need ETH for gas fees. Transactions may fail without sufficient ETH.
              </p>
            </div>
          </div>
        )}
        
        {/* Block limit warning — contract caps at 500 keys per address per block */}
        {displayKeyCount > MAX_KEYS_PER_BLOCK && (
          <div className="mb-3 p-2 rounded-lg bg-red-900/30 border border-red-500/60 flex items-center gap-2">
            <span className="text-red-400 text-base">⛔</span>
            <div className="flex-1">
              <span className="text-xs text-red-300 font-rajdhani">Block Purchase Limit Exceeded</span>
              <p className="text-[10px] text-red-400/70 mt-0.5">
                Max {MAX_KEYS_PER_BLOCK.toString()} keys per transaction. Reduce your amount and try again.
              </p>
            </div>
          </div>
        )}
        {/* MEV warning for large-but-valid purchases */}
        {displayKeyCount > 200n && displayKeyCount <= MAX_KEYS_PER_BLOCK && (
          <div className="mb-3 p-2 rounded-lg bg-blue-900/30 border border-blue-500/50 flex items-center gap-2">
            <span className="text-blue-400 text-base">🤖</span>
            <div className="flex-1">
              <span className="text-xs text-blue-300 font-rajdhani">Large Purchase Detected</span>
              <p className="text-[10px] text-blue-400/70 mt-0.5">
                Large transactions may be targeted by MEV bots. Consider splitting into smaller purchases.
              </p>
            </div>
          </div>
        )}
        
        {/* Contextual game state warnings */}
        <BuyKeysWarnings
          isFOMOSurge={fomoSurgeActive}
          isFlashCrash={flashCrashActive}
          isAssassinMode={isAssassinMode}
          timeLeft={timeRemaining}
          extinctionEta={timeRemaining >= 0 && timeRemaining < 60 ? timeRemaining : undefined}
          isCursed={cascadeLevel > 0}
        />
        
        {/* Buy Button */}
        <motion.button
          onClick={needsApproval && !demoMode.isDemo ? handleApprove : handleBuy}
          disabled={isLoading || isApproving || (!account && !demoMode.isDemo) || (!demoMode.isDemo && displayKeyCount > MAX_KEYS_PER_BLOCK)}
          className={`w-full py-4 rounded-xl font-orbitron text-lg font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            !account && !demoMode.isDemo
              ? 'bg-gray-800 text-gray-500' 
              : needsApproval && !demoMode.isDemo
                ? 'bg-gradient-to-r from-neon-purple to-neon-magenta text-white' 
                : 'btn-buy'
          }`}
          whileHover={account || demoMode.isDemo ? { scale: 1.01 } : {}}
          whileTap={account || demoMode.isDemo ? { scale: 0.99 } : {}}
        >
          {demoMode.isDemo ? (isLoading ? 'Processing...' : '🎮 Buy Demo Keys') : (!account ? 'Connect Wallet' : isLoading ? 'Processing...' : isApproving ? 'Approving...' : needsApproval ? 'Approve PAW' : 'Execute Purchase')}
        </motion.button>
        
        {/* Post-purchase share prompt */}
        {lastPurchaseData && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 p-3 rounded-xl bg-neon-cyan/5 border border-neon-cyan/20 flex items-center justify-between gap-3"
          >
            <span className="text-xs text-gray-400 font-rajdhani">🔑 {lastPurchaseData.keys} keys acquired!</span>
            <SocialSharing 
              data={{ type: 'purchase', keys: lastPurchaseData.keys, amount: lastPurchaseData.amount }}
              playerAddress={account || undefined}
              className="flex-shrink-0"
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

